<template>
  <div>

    <TopSection v-if="detailed" :title="$t('claims')|capitalize">
      <!-- Actions -->

      <template v-slot:actions>
        <div class="mb-4">
          <b-button
            v-b-modal="'claimsModal'"
            class="mr-4 medium-bold"
            @click="setMode('edit_bulk')"
          >
            {{ $t('editClaims') | capitalize }}
          </b-button>
          <b-button
            v-b-modal="'claimsModal'"
            class="mr-4 medium-bold"
            variant="danger"
            @click="setMode('delete')"
          >
            {{ $t('deleteClaims') | capitalize }}
          </b-button>
          <p-check
            :checked="showInactive"
            class="p-icon p-curve p-pulse ineligble-icon"
            color="success"
            @change="showInactive = !showInactive"
          >
            <template v-slot:extra>
              <i class="icon fa fa-check"/>
            </template>
          </p-check>
          <span>{{ $t("show_inactive") | capitalize }}</span>
        </div>
      </template>


    </TopSection>
    <v-server-table
      ref="claimsTable"
      :url="url"
      :columns="columns"
      :options="options"
      @pagination="storePage"
    >
      <!-- RIGHT TYPE -->
      <template v-slot:right_type="{ row }">
        <div class="ml-4 mt-2">
          <div class="table-main-class" v-if="row.showRightType">
            {{ row.right_type }}
          </div>
          <Status
            v-if="row.status == statusTypes.CLAIMED"
            :text="$t('claimed') | capitalize"
            :icon="['fas', 'check-circle']"
            color="green"
          />
          <Status
            v-if="row.status == statusTypes.UNDERCLAIMED"
            :text="$t('underclaimed') | capitalize"
            :icon="['fas', 'question-circle']"
            color="orange"
          />
          <Status
            v-if="row.status == statusTypes.OVERCLAIMED"
            :text="$t('overclaimed') | capitalize"
            :icon="['fas', 'exclamation-circle']"
            color="red"
          />
          <Status
            v-if="row.status == statusTypes.UNCLAIMED"
            :text="$t('unclaimed') | capitalize"
            :icon="['fas', 'exclamation-circle']"
            color="orange"
          />
        </div>
      </template>

      <!-- CREATED -->
      <template v-slot:created="{ row }">
        <div class="table-main-class">
          {{ row.created | transformDate("MMMM Do YYYY") }}
        </div>
        <div class="table-sub-class">
          {{ row.created | transformDate("h:mm a") }}
        </div>
      </template>

      <!-- DELETED -->
      <template v-slot:deleted="{ row }">
        <div v-if="row.deleted" class="table-main-class">
          {{ row.deleted | transformDate("MMMM Do YYYY") }}
        </div>
        <div v-if="row.deleted" class="table-sub-class">
          {{ row.deleted | transformDate("h:mm a") }}
        </div>
      </template>

      <!-- CLAIMER -->
      <template v-slot:claimer="{ row }">
        <div class="d-flex">
          <div class="table-logo">
            <img width="20" src="@/assets/img/mlc.png">
          </div>
          <div>
            <div class="table-main-class">
              {{ row.claimer }}
            </div>
          </div>
        </div>
      </template>

      <!-- RIGHT OWNER -->
      <template v-slot:right_owner="{ row }">
        <div class="table-main-class">
          {{ row.right_owner }}
        </div>
      </template>

      <!-- PAYEE -->
      <template v-slot:payee="{ row }">
        <div class="table-main-class">
          {{ row.payee }}
        </div>
      </template>

      <!-- PERIOD -->
      <template v-slot:right_period="{ row }">
        {{ `${getDate(row.start_date)} - ${getDate(row.end_date)}` }}
      </template>

      <!-- ACTIONS COLUMN -->
      <template v-slot:actions="{ row }">
        <div class="d-flex justify-content-end pr-3">
          <i
            v-if="row.can_edit && !row.deleted && (isAdmin || isOrganizationUser(row))"
            v-b-tooltip.hover
            v-b-modal="'claimsModal'"
            style="color:#6f6f6f; font-size:16px;"
            class="fa fa-pencil pr-4"
            :title="$t('editClaim') | capitalize"
            @click="editClaim(row)"
          />
          <i
            v-if="row.can_edit && !row.deleted && (isAdmin || isOrganizationUser(row))"
            v-b-tooltip.hover
            style="color:#6f6f6f; font-size:16px;"
            class="fa fa-trash clickable"
            :title="$t('removeClaim') | capitalize"
            @click="unclaim(row)"
          />
        </div>
      </template>
    </v-server-table>

    <ClaimsModal
      :sound-recordings="[soundRecording]"
      :initial="currentRow"
      :mode="mode"
      :rightOwners="rightOwners"
      pref="ClaimsModal"
      @close="closeModal"
      @claimEdited="updateTable"
    >
      <template v-slot:details>
        <div class="d-flex flex-column">
          <div class="table-header">
            {{ soundRecording.title }}
          </div>
          <div class="table-sub-class">
            {{ soundRecording.artist }}
          </div>
          <div class="table-sub-class">
            {{ soundRecording.isrc }}
          </div>
        </div>
      </template>
    </ClaimsModal>
  </div>
</template>


<style lang="scss" scoped >
::v-deep tr {
  height: 80px !important;
}
</style>


<script>
import {
  doRequest,
  responsePaginatedAdapter
} from "@/assets/libs/utils"
import { ApiCalls } from "@/assets/libs/services"
import { Event } from "vue-tables-2"
import { capitalize } from "lodash"
import { mapGetters } from "vuex"

export default {
  name: "SoundRecordingDetailPage",

  computed: {
    ...mapGetters("soundsys", ["organization", "isAdmin", "statusTypes"]),
    isOrganizationUser () {
      return ({
        right_owner, claimer, payee
      } = {}) => {
        return (
          (this.organization === right_owner) ||
          (this.organization === claimer) ||
          (this.organization === payee)
        )
      }
    },
    columns () {
      return [
        "right_type",
        "created",
        this.showInactive ? "deleted" : "",
        "claimer",
        "right_owner",
        "payee",
        "split",
        "right_period",
        "actions"
      ]
    },
    now () {
      return this.$options.filters.moment()
    }
  },
  methods: {
    setMode (mode) {
      this.mode = mode
    },
    getDate (date) {
      if (!date || date.length === 0) return " ?"
      return this.$options.filters.moment(date).format("MMM Do YYYY")
    },
    unclaim (row) {
      ApiCalls.unclaim(row.id)
        .then(() => {
          this.$toasted.global.toast(this.$t("recording_unclaimed"))
          this.$refs.claimsTable.refresh()
        })
        .catch(err => {
          this.$toasted.global.toast(err.response.data)
        })
    },
    editClaim (row) {
      this.setMode("edit")
      this.currentRow = row
    },
    storePage (page){
      this.page = page
    },
    updateTable () {
      const { claimsTable } = this.$refs
      claimsTable.refresh()
      claimsTable.setPage(this.page)
    },
    closeModal (modal) {
      this.$nextTick(() => {
        // Wrapped in $nextTick to ensure DOM is rendered before closing
        modal.hide()
      })
    },
  },
  props: {
    soundRecording: {
      type: Object,
      default: () => {}
    },
    soundRecordingId: {
      type: Number,
      default: null
    },
    detailed: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    showInactive (showInactive) {
      Event.$emit("vue-tables.filter::show_inactive", showInactive)
    }
  },
  data () {
    return {
      rightOwners:null,
      mode: "edit",
      showInactive: false,
      page: 1,
      currentRow: {},
      url: `soundrecordings/${this.soundRecordingId || this.$route.params.id}/claims`,
      options: {
        skin: "table table-striped",
        sortable: [],
        perPage: 4,
        customFilters: ["show_inactive"],
        initFilters: { "show_inactive": this.showInactive },
        headings: {
          right_type: capitalize(this.$t("useType")),
          created: capitalize(this.$t("created")),
          deleted: capitalize(this.$t("deleted")),
          claimer: capitalize(this.$t("submittedBy")),
          right_owner: capitalize(this.$t("rightOwner")),
          payee: capitalize(this.$t("payee")),
          split: capitalize(this.$t("claimPercentage")),
          right_period: capitalize(this.$t("rightPeriod")),
          actions: ""
        },
        columnsClasses: {
          right_type: "width-large",
        },
        requestFunction (params) {
          return doRequest(this.url, params)
        },
        responseAdapter: resp => {
          if (resp && resp.data) {
            this.rightOwners = resp.data.right_owners
            this.$emit("mapSoundRecording", resp.data.sound_recording)
            resp.data.results = resp.data.results.reduce((acc, next) => {
              acc.push({
                status: next.status,
                right_type: next.right_type,
                showRightType: true,
                ...next.claims[0]
              })
              if (next.claims.length > 1) {
                next.claims.slice(1).forEach(claim => {
                  acc.push({
                    status: "",
                    right_type: next.right_type,
                    ...claim
                  })
                })
              }
              return acc
            }, [])
          }
          return responsePaginatedAdapter(resp)
        }
      }
    }
  },
}
</script>
